import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Shield02Icon,
  Copy01Icon,
  CheckmarkCircle01Icon,
  BankIcon,
  Calendar01Icon,
  AlertCircleIcon,
  ArrowRight01Icon,
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import { api } from '../utils/api';
import { FadeLoader } from 'react-spinners';
import ReelText from '../components/ui/ReelText';

const AgentProfilePage = () => {
  const { agentSlug } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCopiedLink, setShowCopiedLink] = useState(false);
  const [copiedBankIndex, setCopiedBankIndex] = useState(null);

  useEffect(() => {
    const fetchAgentProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/agent-profile/${agentSlug}`);
        setAgent(response.data?.data?.agent || response.data?.agent);
      } catch (err) {
        console.error('Failed to fetch agent profile:', err);
        setError(err.response?.data?.message || 'Agent profile not found');
      } finally {
        setLoading(false);
      }
    };

    if (agentSlug) fetchAgentProfile();
  }, [agentSlug]);

  const handleCopyProfileLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setShowCopiedLink(true);
    setTimeout(() => setShowCopiedLink(false), 2000);
  };

  const handleCopyAccountNumber = (index, accountNumber) => {
    navigator.clipboard.writeText(accountNumber);
    setCopiedBankIndex(index);
    setTimeout(() => setCopiedBankIndex(null), 2000);
  };

  const handleStartTransaction = (orderToken) => {
    navigate(`/order/${orderToken}`);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FadeLoader color="#4ade80" />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="max-w-lg mx-auto pb-20 px-4">
        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl p-10 rounded-[3rem] border border-red-200 dark:border-red-800/30 shadow-float-lg text-center">
          <div className="bg-red-500/10 p-8 rounded-full inline-block mb-6 text-red-500 animate-pulse">
            <HugeiconsIcon icon={AlertCircleIcon} size={64} />
          </div>
          <h1 className="text-3xl font-black text-charcoal dark:text-white mb-4">Profile Not Found</h1>
          <p className="text-muted-gray text-lg font-medium mb-8">
            {error || 'This agent profile does not exist or has been removed.'}
          </p>
          <Button onClick={() => navigate('/')} variant="secondary" className="w-full py-4 rounded-2xl font-black">
            Back to Safety
          </Button>
        </div>
      </div>
    );
  }

  const profileUrl = window.location.href;

  return (
    <div className="max-w-4xl mx-auto pb-20 px-4 sm:px-6">
      {/* Profile Header */}
      <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl p-6 sm:p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg mb-6">
        <div className="flex flex-col items-center text-center">
          {/* Avatar */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-soft-green/10 text-soft-green flex items-center justify-center text-3xl sm:text-4xl font-black uppercase mb-4">
            {(agent.name || 'A')[0]}
          </div>

          {/* Name & Verified Badge */}
          <div className="flex items-center gap-2 mb-2 flex-wrap justify-center">
            <h1 className="text-2xl sm:text-3xl font-black text-charcoal dark:text-white">
              <ReelText text={agent.name} />
            </h1>
            {agent.is_verified && (
              <div className="flex items-center gap-1">
                <HugeiconsIcon icon={Shield02Icon} size={20} className="text-soft-green" />
              </div>
            )}
          </div>

          {/* Verified Badge Text */}
          {agent.is_verified && (
            <div className="flex items-center gap-1 mb-4">
              <span className="text-[10px] font-black uppercase text-soft-green tracking-widest">Verified Agent</span>
            </div>
          )}

          {/* Bio */}
          {agent.bio && (
            <p className="text-muted-gray text-sm font-medium mb-6 max-w-md mx-auto">{agent.bio}</p>
          )}

          {/* Share Profile Link */}
          <div className="w-full bg-off-white/50 dark:bg-gray-800/50 p-4 rounded-2xl border border-light-gray/20 dark:border-gray-700/20 mb-6">
            <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-2">Share Agent Profile</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={profileUrl}
                readOnly
                className="flex-1 bg-white dark:bg-dark-surface border border-light-gray/20 dark:border-gray-700 rounded-xl px-4 py-2 text-xs font-mono text-charcoal dark:text-white outline-none truncate"
              />
              <button
                onClick={handleCopyProfileLink}
                className={`p-3 rounded-xl transition-all flex-shrink-0 ${showCopiedLink ? 'bg-soft-green text-white' : 'bg-soft-green/10 text-soft-green hover:bg-soft-green/20'}`}
              >
                <HugeiconsIcon icon={showCopiedLink ? CheckmarkCircle01Icon : Copy01Icon} size={20} />
              </button>
            </div>
          </div>

          {/* Info Grid - Joined & Status */}
          <div className="w-full grid grid-cols-2 gap-3 sm:gap-4 mb-6">
            <div className="bg-off-white/50 dark:bg-gray-800/50 p-4 rounded-2xl border border-light-gray/20 dark:border-gray-700/20 text-left">
              <div className="flex items-center gap-2 mb-2">
                <HugeiconsIcon icon={Calendar01Icon} size={16} className="text-muted-gray" />
                <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest">Joined</p>
              </div>
              <p className="text-sm font-bold text-charcoal dark:text-white">{agent.member_since}</p>
            </div>
            <div className="bg-off-white/50 dark:bg-gray-800/50 p-4 rounded-2xl border border-light-gray/20 dark:border-gray-700/20 text-left">
              <div className="flex items-center gap-2 mb-2">
                <HugeiconsIcon icon={Calendar01Icon} size={16} className="text-muted-gray" />
                <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest">Status</p>
              </div>
              <p className="text-sm font-bold text-charcoal dark:text-white">
                {agent.is_verified ? 'Verified' : 'Active'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Accounts */}
      {/*{agent.bank_accounts && agent.bank_accounts.length > 0 && (
        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm overflow-hidden mb-6">
          <div className="p-6 border-b border-light-gray/20 dark:border-dark-border/20">
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={BankIcon} size={20} className="text-soft-green" />
              <h2 className="text-xl font-black text-charcoal dark:text-white">
                <ReelText text="Bank Accounts" />
              </h2>
            </div>
            <p className="text-xs text-muted-gray mt-1">Select account for your transaction</p>
          </div>
          <div className="divide-y divide-light-gray/10 dark:divide-dark-border/10">
            {agent.bank_accounts.map((bank, index) => (
              <div key={index} className="p-4 sm:p-6 hover:bg-soft-green/5 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-3 rounded-xl bg-soft-green/10 text-soft-green flex-shrink-0">
                      <HugeiconsIcon icon={BankIcon} size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base sm:text-lg font-black text-charcoal dark:text-white mb-1">
                        {bank.bank_name}
                      </p>
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-sm sm:text-base font-mono font-bold text-charcoal dark:text-white">
                          {bank.account_number}
                        </code>
                        <button
                          onClick={() => handleCopyAccountNumber(index, bank.account_number)}
                          className="text-soft-green flex-shrink-0"
                        >
                          <HugeiconsIcon icon={copiedBankIndex === index ? CheckmarkCircle01Icon : Copy01Icon} size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-muted-gray">{bank.account_name}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}*/}

      {/* Active Orders */}
      {agent.active_orders && agent.active_orders.length > 0 && (
        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm overflow-hidden mb-6">
          <div className="p-6 border-b border-light-gray/20 dark:border-dark-border/20">
            <h2 className="text-xl font-black text-charcoal dark:text-white">
              <ReelText text="Active Orders" />
            </h2>
            <p className="text-xs text-muted-gray mt-1">Select an order to start your transaction</p>
          </div>
          <div className="divide-y divide-light-gray/10 dark:divide-dark-border/10">
            {agent.active_orders.map((order) => (
              <div
                key={order.link_token}
                className="p-4 sm:p-6 hover:bg-soft-green/5 transition-colors cursor-pointer"
                onClick={() => handleStartTransaction(order.link_token)}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className={`p-3 rounded-xl flex-shrink-0 ${order.transaction_type === 'deposit' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'}`}>
                      <HugeiconsIcon icon={BankIcon} size={20} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider flex-shrink-0 ${
                          order.transaction_type === 'deposit' ? 'bg-blue-500/10 text-blue-600' : 'bg-purple-500/10 text-purple-600'
                        }`}>
                          {order.transaction_type}
                        </span>
                        <p className="text-sm font-bold text-charcoal dark:text-white truncate">Order #{order.link_token}</p>
                      </div>
                      <p className="text-xs text-muted-gray mt-1">
                        Rate: ₦{parseFloat(order.current_rate).toLocaleString()} per $1
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-muted-gray">Limits</p>
                      <p className="text-sm font-black text-charcoal dark:text-white">
                        ${parseFloat(order.min_amount_usd).toFixed(2)} - ${parseFloat(order.total_amount_usd).toFixed(2)}
                      </p>
                    </div>
                    <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="text-muted-gray" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Active Orders */}
      {(!agent.active_orders || agent.active_orders.length === 0) && (
        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-sm p-8 sm:p-12 text-center">
          <HugeiconsIcon icon={AlertCircleIcon} size={48} className="text-muted-gray mx-auto mb-4" />
          <h3 className="text-xl font-black text-charcoal dark:text-white mb-2">No Active Orders</h3>
          <p className="text-muted-gray text-sm">This agent currently has no active orders. Please check back later.</p>
        </div>
      )}
    </div>
  );
};

export default AgentProfilePage;
